/**
 * Authorization definitions for the Engagement Letter Database app.
 *
 * Each portal app owns its resources, actions and roles here. The portal engine
 * (`src/core/auth/permissions`) stays generic; this file supplies the concrete
 * role → permission map that the portal registry merges in.
 *
 * Role names must match the Azure AD app-role values configured on the portal's
 * app registration. They are app-prefixed (`ELD.*`) so roles never collide with
 * roles from other portal apps.
 */
import type { RolesWithPermissions } from '@/core/auth/permissions/types';

/** Minimal shape of a client engagement record used for ownership checks. */
export interface EngagementRecordResource {
  createdBy?: string | null;
  updatedBy?: string | null;
}

/** Minimal shape of a waiver record used for ownership checks. */
export interface WaiverRecordResource {
  createdBy?: string | null;
  updatedBy?: string | null;
}

/** Azure AD app-role values recognised by this app. */
export const ENGAGEMENT_LETTER_DATABASE_ROLES = {
  admin: 'ELD.Admin',
  editor: 'ELD.Editor',
  contributor: 'ELD.Contributor',
  reader: 'ELD.Reader',
} as const;

export type EngagementLetterDatabaseRole =
  (typeof ENGAGEMENT_LETTER_DATABASE_ROLES)[keyof typeof ENGAGEMENT_LETTER_DATABASE_ROLES];

/** Resources this app protects and the actions available on each. */
export type EngagementLetterDatabasePermissions = {
  EngagementRecords: {
    dataType: EngagementRecordResource;
    action: 'view' | 'create' | 'update' | 'delete';
  };
  WaiverRecords: {
    dataType: WaiverRecordResource;
    action: 'view' | 'create' | 'update' | 'delete';
  };
};

/** Owner-only predicate: the acting user created the record. */
const isCreator = (user: { mpId: string }, data: { createdBy?: string | null }): boolean =>
  data.createdBy != null && data.createdBy === user.mpId;

export const engagementLetterDatabaseRolePermissions: RolesWithPermissions<
  EngagementLetterDatabaseRole,
  EngagementLetterDatabasePermissions
> = {
  'ELD.Admin': {
    EngagementRecords: { view: true, create: true, update: true, delete: true },
    WaiverRecords: { view: true, create: true, update: true, delete: true },
  },
  'ELD.Editor': {
    EngagementRecords: { view: true, create: true, update: true, delete: true },
    WaiverRecords: { view: true, create: true, update: true, delete: false },
  },
  'ELD.Contributor': {
    EngagementRecords: { view: true, create: true, update: isCreator, delete: false },
    WaiverRecords: { view: true, create: true, update: isCreator, delete: false },
  },
  'ELD.Reader': {
    EngagementRecords: { view: true, create: false, update: false, delete: false },
    WaiverRecords: { view: true, create: false, update: false, delete: false },
  },
};
